import {
  endOfDay,
  endOfQuarter,
  endOfYear,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfQuarter,
  startOfYear,
  subDays,
} from 'date-fns';
import { es } from 'date-fns/locale';

export const DATE_RANGE_PRESETS = ['hoy', '7d', '30d', 'trimestre', 'anio', 'personalizado'] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];

export interface DateRange {
  from: Date;
  to: Date;
  preset: DateRangePreset;
}

export const PRESET_LABELS: Record<DateRangePreset, string> = {
  hoy: 'Hoy',
  '7d': 'Últimos 7 días',
  '30d': 'Últimos 30 días',
  trimestre: 'Este trimestre',
  anio: 'Este año',
  personalizado: 'Personalizado',
};

/** Resolves a preset against `reference` so ranges follow the calendar, not the clock. */
export function resolveDateRange(
  preset: Exclude<DateRangePreset, 'personalizado'>,
  reference: Date = new Date(),
): DateRange {
  switch (preset) {
    case 'hoy':
      return { from: startOfDay(reference), to: endOfDay(reference), preset };
    case '7d':
      return { from: startOfDay(subDays(reference, 6)), to: endOfDay(reference), preset };
    case '30d':
      return { from: startOfDay(subDays(reference, 29)), to: endOfDay(reference), preset };
    case 'trimestre':
      return { from: startOfQuarter(reference), to: endOfQuarter(reference), preset };
    case 'anio':
      return { from: startOfYear(reference), to: endOfYear(reference), preset };
  }
}

export const toInputValue = (date: Date) => format(date, 'yyyy-MM-dd');

export function fromInputValue(raw: string, edge: 'from' | 'to'): Date | null {
  const parsed = parseISO(raw);
  if (!isValid(parsed)) return null;
  return edge === 'from' ? startOfDay(parsed) : endOfDay(parsed);
}

/** `1 – 30 de sep`, collapsing the parts both ends share. */
export function formatDateRange({ from, to, preset }: DateRange): string {
  if (preset !== 'personalizado') return PRESET_LABELS[preset];

  const sameYear = from.getFullYear() === to.getFullYear();
  const sameMonth = sameYear && from.getMonth() === to.getMonth();
  const thisYear = sameYear && from.getFullYear() === new Date().getFullYear();

  if (sameMonth) {
    const tail = thisYear ? "d 'de' MMM" : "d 'de' MMM yyyy";
    return `${format(from, 'd', { locale: es })} – ${format(to, tail, { locale: es })}`;
  }

  const pattern = thisYear ? "d 'de' MMM" : "d 'de' MMM yyyy";
  return `${format(from, pattern, { locale: es })} – ${format(to, pattern, { locale: es })}`;
}

/** URL-safe form so a report link carries its own window. */
export const serializeDateRange = (range: DateRange) =>
  range.preset === 'personalizado'
    ? `${toInputValue(range.from)}..${toInputValue(range.to)}`
    : range.preset;

export function parseDateRange(raw: string | null, fallback: DateRange): DateRange {
  if (!raw) return fallback;
  if (raw.includes('..')) {
    const [rawFrom = '', rawTo = ''] = raw.split('..');
    const from = fromInputValue(rawFrom, 'from');
    const to = fromInputValue(rawTo, 'to');
    if (from && to) return { from, to, preset: 'personalizado' };
    return fallback;
  }
  const preset = DATE_RANGE_PRESETS.find((p) => p === raw && p !== 'personalizado');
  return preset && preset !== 'personalizado' ? resolveDateRange(preset) : fallback;
}
